import { useState } from "react";
import {
  GraduationCap, Award, HeartHandshake, Instagram, Sparkles,
  MapPin, Quote, Stethoscope,
} from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { TEAM, CLINIC, COMMITMENT } from "@/lib/clinic";

function PhotoCard({ member, testId }) {
  return (
    <div
      className="relative overflow-hidden rounded-[2rem] border border-stone-200 bg-[#f2f0eb] aspect-[3/4] soft-shadow"
      data-testid={testId}
    >
      <img
        src={member.photo}
        alt={member.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-[center_top] transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
      />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 bg-gradient-to-t from-[#0e1512]/92 via-[#0e1512]/50 to-transparent text-white">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4B27E] font-medium">
          Co-Founder · Dental Surgeon
        </div>
        <div className="mt-1 font-display text-2xl md:text-3xl font-semibold tracking-tight">
          {member.name}
        </div>
        <div className="mt-1 text-white/70 text-xs sm:text-sm flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />
          {member.experience} · Since {member.completedYear}
        </div>
      </div>
    </div>
  );
}

function BioCard({ member, testId }) {
  return (
    <div
      className="relative rounded-[2rem] bg-[#2D4A3E] text-white p-6 sm:p-8 aspect-[3/4] flex flex-col overflow-hidden soft-shadow-lg"
      data-testid={testId}
    >
      <div aria-hidden className="absolute -top-14 -right-14 w-56 h-56 rounded-full bg-[#D4B27E]/20 blur-3xl" />
      <div aria-hidden className="absolute -bottom-14 -left-14 w-56 h-56 rounded-full bg-[#D4B27E]/10 blur-3xl" />
      <div className="relative flex-1 min-h-0 overflow-y-auto">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#D4B27E] text-[#1C221F] flex items-center justify-center">
            <Stethoscope className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4B27E] font-medium">
              {member.designation.replace(/^BDS\s*·\s*/i, "")}
            </div>
            <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
              {member.name}
            </div>
            <div className="mt-0.5 text-white/60 text-xs flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />
              {member.experience} · Since {member.completedYear}
            </div>
          </div>
        </div>
        <p className="mt-5 text-sm text-white/90 leading-relaxed">{member.bio}</p>
        <p className="mt-3 text-xs text-white/60 leading-relaxed">{member.training}</p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {member.focus.map((f) => (
            <span
              key={f}
              className="text-[11px] bg-white/10 border border-white/15 rounded-full px-2.5 py-1 text-white/90"
            >
              {f}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Doctor() {
  const [hovered, setHovered] = useState(null); // "malika" | "ravi" | null
  const [copyRef, copyVis] = useReveal();
  const [gridRef, gridVis] = useReveal();
  const [quoteRef, quoteVis] = useReveal();

  const malika = TEAM[0];
  const ravi = TEAM[1];

  const leftIsBio = hovered === "ravi";   // hover Ravi → left slot shows Ravi's bio
  const rightIsBio = hovered === "malika"; // hover Malika → right slot shows Malika's bio

  return (
    <section id="doctor" data-testid="section-doctor" className="relative py-24 md:py-32 bg-white overflow-hidden">
      <div aria-hidden className="absolute top-1/2 -translate-y-1/2 -left-40 w-[520px] h-[520px] rounded-full bg-[#D4B27E]/10 blur-3xl float-slow" />
      <div aria-hidden className="absolute -top-24 right-0 w-[400px] h-[400px] rounded-full bg-[#2D4A3E]/[0.05] blur-3xl" />

      <div className="container-x relative">
        <div
          ref={copyRef}
          className={`max-w-3xl mx-auto text-center reveal ${copyVis ? "is-visible" : ""}`}
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#2D4A3E] font-medium">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} /> Meet our doctors
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C221F] tracking-tight leading-[1.05]">
            Two dentists.{" "}
            <span className="shine">One shared vision.</span>
            <br /> Your best smile.
          </h2>
          <p className="mt-5 text-[#57605B] leading-relaxed">
            Co-founded on <span className="font-medium text-[#1C221F]">24th May 2014</span> by
            Dr. Malika and Dr. Ravi Kant — both dental surgeons who trained at Fortis La Femme
            Hospital, New Delhi before returning home to serve Ratia and the surrounding region.
          </p>
          <p className="mt-3 text-xs text-[#57605B]/80">
            Hover over a photo — the other side reveals their story.
          </p>
        </div>

        {/* Two side-by-side slots that swap on hover */}
        <div
          ref={gridRef}
          className={`mt-12 md:mt-16 grid md:grid-cols-2 gap-5 md:gap-8 max-w-5xl mx-auto reveal ${gridVis ? "is-visible" : ""}`}
        >
          {/* LEFT SLOT — Dr. Malika by default; swaps to Ravi's bio when Ravi is hovered */}
          <div
            className="relative group"
            onMouseEnter={() => setHovered("malika")}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered("malika")}
            onBlur={() => setHovered(null)}
            tabIndex={0}
            data-testid="doctor-slot-left"
          >
            <div
              className={`transition-all duration-500 ease-out ${
                leftIsBio ? "opacity-0 -translate-y-2 pointer-events-none absolute inset-0" : "opacity-100 translate-y-0"
              }`}
            >
              <PhotoCard member={malika} testId="team-member-malika" />
            </div>
            <div
              className={`transition-all duration-500 ease-out ${
                leftIsBio ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none absolute inset-0"
              }`}
              aria-hidden={!leftIsBio}
            >
              <BioCard member={ravi} testId="team-member-ravi-bio" />
            </div>
          </div>

          {/* RIGHT SLOT — Dr. Ravi Kant by default; swaps to Malika's bio when Malika is hovered */}
          <div
            className="relative group"
            onMouseEnter={() => setHovered("ravi")}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered("ravi")}
            onBlur={() => setHovered(null)}
            tabIndex={0}
            data-testid="doctor-slot-right"
          >
            <div
              className={`transition-all duration-500 ease-out ${
                rightIsBio ? "opacity-0 -translate-y-2 pointer-events-none absolute inset-0" : "opacity-100 translate-y-0"
              }`}
            >
              <PhotoCard member={ravi} testId="team-member-ravi" />
            </div>
            <div
              className={`transition-all duration-500 ease-out ${
                rightIsBio ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none absolute inset-0"
              }`}
              aria-hidden={!rightIsBio}
            >
              <BioCard member={malika} testId="team-member-malika-bio" />
            </div>
          </div>
        </div>

        {/* Our Commitment */}
        <div
          ref={quoteRef}
          className={`mt-16 md:mt-20 max-w-4xl mx-auto reveal ${quoteVis ? "is-visible" : ""}`}
        >
          <div className="relative bg-[#2D4A3E] text-white rounded-[2.25rem] p-8 md:p-12 overflow-hidden">
            <div aria-hidden className="absolute -top-10 -right-10 w-64 h-64 rounded-full bg-[#D4B27E]/25 blur-3xl" />
            <Quote className="w-8 h-8 text-[#D4B27E]" strokeWidth={1.5} />
            <p className="mt-6 font-display text-2xl md:text-3xl leading-[1.25] tracking-tight">
              &ldquo;<span className="shine">{COMMITMENT.quote}</span>&rdquo;
            </p>
            <p className="mt-6 text-white/75 text-sm md:text-base leading-relaxed max-w-3xl">
              {COMMITMENT.body}
            </p>
            <p className="mt-4 text-white/60 text-sm md:text-base leading-relaxed max-w-3xl">
              {COMMITMENT.short}
            </p>
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {[
            { icon: GraduationCap, title: "Dental Surgeons", sub: "Practising since 2011" },
            { icon: Award, title: `${CLINIC.yearsOfCare} years`, sub: `Since ${CLINIC.establishedYear}` },
            { icon: HeartHandshake, title: "50,000+", sub: "Smiles restored" },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl border border-stone-200 bg-white p-5 text-center soft-shadow">
              <c.icon className="w-6 h-6 text-[#2D4A3E] mx-auto" strokeWidth={1.5} />
              <div className="mt-3 font-display font-semibold text-[#1C221F]">{c.title}</div>
              <div className="text-sm text-[#57605B]">{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <a
            href={CLINIC.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-instagram-doctor"
            className="inline-flex items-center gap-2 text-sm text-[#2D4A3E] hover:text-[#21362D] font-medium border border-[#2D4A3E]/20 rounded-full px-5 py-2.5 hover:bg-[#2D4A3E]/5 transition-colors"
          >
            <Instagram className="w-4 h-4" strokeWidth={1.75} />
            Follow us on Instagram {CLINIC.instagramHandle}
          </a>
        </div>
      </div>
    </section>
  );
}
